/**
 * NavigationRenderers — breadcrumb and custom-HTML components.
 *
 * Extracts SharedBreadcrumbComponent (with BreadcrumbProperties,
 * breadcrumbIconMap, BreadcrumbItem, BreadcrumbEditorPlaceholder helpers)
 * and SharedCustomComponent from SharedComponentRenderer.tsx.
 *
 * Step 8 of the decomposition roadmap. No unsafe casts are present
 * in these components; the only casts used are `Record<string, unknown>`
 * (acceptable) for dynamic property lookup on responsive style objects.
 */
import React, { useMemo, useCallback } from "react";
import { Component, ComponentType, Viewport } from "@/types";
import { cn } from "@/lib/utils";
import { ChevronRight, Home, LayoutDashboard } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useBreadcrumbs,
  ProjectBreadcrumbItem,
} from "@/hooks/navigation/useBreadcrumbs";
import { normalizeLegacySemanticStyleColor } from "@/utils/theme-aware-legacy-colors";
import DOMPurify from "dompurify";

export interface NavigationRendererProps {
  component: Component;
  viewport?: Viewport;
}

// ─────────────────────────────────────────────────────────────────────────────
// BreadcrumbProperties
// ─────────────────────────────────────────────────────────────────────────────

interface BreadcrumbProperties {
  showDashboardLink?: boolean;
  showHomeIcon?: boolean;
  showIcons?: boolean;
  separatorIcon?: string;
  backgroundColor?: string;
  textColor?: string;
  hoverColor?: string;
  activeColor?: string;
  fontSize?: number | string;
  padding?: string | number;
  gap?: number;
  maxItemWidth?: number | string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Icon map
// ─────────────────────────────────────────────────────────────────────────────

const breadcrumbIconMap: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  Home,
  LayoutDashboard,
};

const BREADCRUMB_MUTED_COLOR = "hsl(var(--muted-foreground))";
const BREADCRUMB_FOREGROUND_COLOR = "hsl(var(--foreground))";

function resolveBreadcrumbColor(
  value: string | undefined,
  fallback: string,
): string {
  if (!value) return fallback;

  return normalizeLegacySemanticStyleColor("color", value, {
    componentType: ComponentType.BREADCRUMB,
  });
}

function getBreadcrumbColors(props: BreadcrumbProperties) {
  const textColor = resolveBreadcrumbColor(
    props.textColor,
    BREADCRUMB_MUTED_COLOR,
  );

  return {
    textColor,
    separatorColor: textColor,
    hoverColor: resolveBreadcrumbColor(
      props.hoverColor,
      BREADCRUMB_FOREGROUND_COLOR,
    ),
    activeColor: resolveBreadcrumbColor(
      props.activeColor,
      BREADCRUMB_FOREGROUND_COLOR,
    ),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// BreadcrumbItem
// ─────────────────────────────────────────────────────────────────────────────

const BreadcrumbItem: React.FC<{
  item: ProjectBreadcrumbItem;
  isFirst: boolean;
  onNavigate: (item: ProjectBreadcrumbItem) => void;
  props: BreadcrumbProperties;
}> = ({ item, isFirst, onNavigate, props }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigate(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onNavigate(item);
    }
  };

  const IconComponent = item.icon ? breadcrumbIconMap[item.icon] : null;
  const showIcon = props.showIcons !== false && IconComponent;
  const colors = getBreadcrumbColors(props);

  return (
    <li className="flex items-center">
      {/* Separator */}
      {!isFirst && (
        <ChevronRight
          className="mx-1 h-4 w-4 flex-shrink-0"
          style={{ color: colors.separatorColor }}
          aria-hidden="true"
        />
      )}

      <div className="flex items-center relative">
        {item.href && !item.isCurrentPage ? (
          <Link
            to={item.href}
            onClick={handleClick}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-forest-green-500 focus:ring-offset-1",
            )}
            style={{
              color:
                item.type === "project-home"
                  ? colors.hoverColor
                  : colors.textColor,
            }}
          >
            {showIcon && <IconComponent className="h-4 w-4 flex-shrink-0" />}
            <span
              className="truncate"
              style={{ maxWidth: props.maxItemWidth || 150 }}
            >
              {item.label}
            </span>
          </Link>
        ) : (
          <span
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded",
              item.isCurrentPage
                ? "font-medium bg-muted/50"
                : "cursor-pointer hover:bg-accent",
            )}
            style={{
              color: item.isCurrentPage ? colors.activeColor : colors.textColor,
            }}
            aria-current={item.isCurrentPage ? "page" : undefined}
            tabIndex={item.isCurrentPage ? undefined : 0}
            onClick={item.isCurrentPage ? undefined : handleClick}
            onKeyDown={item.isCurrentPage ? undefined : handleKeyDown}
          >
            {showIcon && <IconComponent className="h-4 w-4 flex-shrink-0" />}
            <span
              className="truncate"
              style={{ maxWidth: props.maxItemWidth || 150 }}
            >
              {item.label}
            </span>
          </span>
        )}
      </div>
    </li>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BreadcrumbEditorPlaceholder
// ─────────────────────────────────────────────────────────────────────────────

const BreadcrumbEditorPlaceholder: React.FC<{
  props: BreadcrumbProperties;
}> = ({ props }) => {
  const colors = getBreadcrumbColors(props);

  return (
    <nav
      aria-label="Breadcrumb Preview"
      className="flex items-center space-x-1 text-sm border-b"
      style={{
        backgroundColor: props.backgroundColor || "#ffffff",
        padding: props.padding || "8px 16px",
        borderColor: "#e5e7eb",
      }}
    >
      <ol
        className="flex items-center space-x-1 flex-wrap"
        style={{ gap: props.gap || 4 }}
      >
        {props.showDashboardLink === true && (
          <li className="flex items-center">
            <span
              className="flex items-center gap-1.5 px-2 py-1 rounded"
              style={{
                color: colors.textColor,
                fontSize: props.fontSize || 14,
              }}
            >
              {props.showIcons !== false && (
                <LayoutDashboard className="h-4 w-4" />
              )}
              Dashboard
            </span>
          </li>
        )}
        <li className="flex items-center">
          <ChevronRight
            className="mx-1 h-4 w-4"
            style={{ color: colors.separatorColor }}
          />
          <span
            className="flex items-center gap-1.5 px-2 py-1 rounded"
            style={{
              color: colors.hoverColor,
              fontSize: props.fontSize || 14,
            }}
          >
            {props.showHomeIcon !== false && <Home className="h-4 w-4" />}
            Home
          </span>
        </li>
        <li className="flex items-center">
          <ChevronRight
            className="mx-1 h-4 w-4"
            style={{ color: colors.separatorColor }}
          />
          <span
            className="flex items-center gap-1.5 px-2 py-1 rounded font-medium bg-muted/50"
            style={{
              color: colors.activeColor,
              fontSize: props.fontSize || 14,
            }}
          >
            Current Page
          </span>
        </li>
      </ol>
    </nav>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BreadcrumbRenderer (was SharedBreadcrumbComponent)
// ─────────────────────────────────────────────────────────────────────────────

const BreadcrumbRenderer: React.FC<{
  component: Component;
  viewport?: Viewport;
}> = ({ component, viewport }) => {
  const { properties = {}, style = {}, responsivePosition } = component;
  const props = properties as BreadcrumbProperties;

  // Detect viewer mode
  const isViewerMode =
    typeof window !== "undefined" &&
    window.location.pathname.includes("/viewer/");

  // Get breadcrumb data from hook (returns empty in non-viewer contexts)
  const { breadcrumbs, handleBreadcrumbClick } = useBreadcrumbs();

  // Resolve a style property across breakpoint → component style → fallback.
  // Uses `Record<string, unknown>` because the
  // property name is a runtime string and React.CSSProperties does not allow
  // index-signature access.
  const getResponsiveStyle = useCallback(
    (propName: string, defaultValue: unknown): unknown => {
      if (!viewport || !responsivePosition?.breakpoints) return defaultValue;
      const breakpoint =
        viewport === Viewport.MOBILE
          ? "mobile"
          : viewport === Viewport.TABLET
            ? "tablet"
            : "desktop";
      const bpStyle = responsivePosition.breakpoints[breakpoint]?.style;
      return (
        (bpStyle as Record<string, unknown>)?.[propName] ??
        (style as Record<string, unknown>)?.[propName] ??
        defaultValue
      );
    },
    [viewport, responsivePosition, style],
  );

  // Filter breadcrumbs based on properties
  const filteredBreadcrumbs = useMemo(() => {
    if (!breadcrumbs.length) return [];

    let result = [...breadcrumbs];

    // Only show dashboard link if explicitly enabled
    if (props.showDashboardLink !== true) {
      result = result.filter((item) => item.type !== "app");
    }

    return result;
  }, [breadcrumbs, props.showDashboardLink]);

  // In editor mode or if no breadcrumbs, show placeholder
  if (!isViewerMode || filteredBreadcrumbs.length === 0) {
    return <BreadcrumbEditorPlaceholder props={props} />;
  }

  // Viewer mode: render dynamic breadcrumbs
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center space-x-1 text-sm"
      style={{
        backgroundColor: getResponsiveStyle(
          "backgroundColor",
          props.backgroundColor || "transparent",
        ) as string,
        padding: getResponsiveStyle(
          "padding",
          props.padding || "4px 16px",
        ) as string,
        fontSize: getResponsiveStyle(
          "fontSize",
          props.fontSize || 14,
        ) as number,
      }}
    >
      <ol
        className="flex items-center space-x-1 flex-wrap"
        style={{ gap: getResponsiveStyle("gap", props.gap || 4) as number }}
      >
        {filteredBreadcrumbs.map((item, index) => (
          <BreadcrumbItem
            key={item.id}
            item={item}
            isFirst={index === 0}
            onNavigate={handleBreadcrumbClick}
            props={props}
          />
        ))}
      </ol>
    </nav>
  );
};

BreadcrumbRenderer.displayName = "BreadcrumbRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// CustomRenderer (was SharedCustomComponent)
// ─────────────────────────────────────────────────────────────────────────────

const DOMPURIFY_ALLOWED_TAGS = [
  "div",
  "span",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "a",
  "img",
  "strong",
  "em",
  "br",
  "table",
  "thead",
  "tbody",
  "tr",
  "td",
  "th",
] as const;

const DOMPURIFY_ALLOWED_ATTR = [
  "href",
  "src",
  "alt",
  "class",
  "style",
  "id",
] as const;

const CustomRenderer: React.FC<{ component: Component }> = React.memo(
  ({ component }) => {
    const { properties = {}, style } = component;

    // Sanitize HTML content to prevent XSS
    const sanitizeHtml = (htmlContent: string): string =>
      DOMPurify.sanitize(htmlContent, {
        ALLOWED_TAGS: [...DOMPURIFY_ALLOWED_TAGS],
        ALLOWED_ATTR: [...DOMPURIFY_ALLOWED_ATTR],
      });

    if (typeof properties.htmlContent === "string") {
      return (
        <div
          className="w-full h-full custom-component"
          style={{
            ...style,
            padding:
              typeof properties.padding === "string" ||
              typeof properties.padding === "number"
                ? properties.padding
                : 0,
          }}
          // HTML is sanitized by sanitizeHtml immediately before rendering.
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(properties.htmlContent),
          }}
        />
      );
    }

    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{
          ...style,
          backgroundColor:
            typeof properties.backgroundColor === "string"
              ? properties.backgroundColor
              : "#f3f4f6",
          border:
            typeof properties.border === "string"
              ? properties.border
              : "1px solid #e5e7eb",
          borderRadius:
            typeof properties.borderRadius === "string" ||
            typeof properties.borderRadius === "number"
              ? properties.borderRadius
              : 4,
        }}
      >
        <span className="text-gray-500">
          {typeof properties.label === "string"
            ? properties.label
            : "Custom Component"}
        </span>
      </div>
    );
  },
);

CustomRenderer.displayName = "CustomRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// Public dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export function NavigationRenderers({
  component,
  viewport,
}: NavigationRendererProps): JSX.Element {
  if (component.type === ComponentType.BREADCRUMB) {
    return <BreadcrumbRenderer component={component} viewport={viewport} />;
  }
  // ComponentType.CUSTOM
  return <CustomRenderer component={component} />;
}
